<template>
  <v-combobox
    v-bind="$attrs"
    v-model:search="searchQuery"
    v-no-password-manager
    autocapitalize="off"
    :autocomplete="identifier"
    autocorrect="off"
    class="combobox pointer-events-auto hide-icon"
    :custom-filter="filterItems"
    hide-details
    :hide-no-data="hideNoData"
    hide-selected
    item-title="email"
    item-value="email"
    :items="emailSuggestionList"
    :label="i18n.t('labels.email')"
    menu-icon=""
    :menu-props="{ maxHeight: 400, maxWidth: '100%' }"
    :name="identifier"
    :return-object="false"
    type="email"
    variant="outlined"
    @focus="onFocus"
    @update:model-value="updateModelValue"
  >
    <template #prepend-inner><slot name="prepend-inner" /></template>

    <template #no-data>
      <v-list-item v-if="!loading && filteredUnits.length === 0">
        <v-list-item-title>
          {{ i18n.t('labels.autocomplete_no_data') }}
        </v-list-item-title>
      </v-list-item>
    </template>

    <template #item="{ props, internalItem: item }">
      <v-list-subheader
        v-if="
          item.raw?.header?.type === 'subheader' &&
          item.raw?.header?.hasEmailMatches &&
          emailSuggestionList.some((e) => e.email)
        "
      >
        {{ i18n.t(item.raw?.header?.title) }}
      </v-list-subheader>
      <v-divider v-if="item.value?.type === 'divider'" />
      <v-list-item v-if="item.raw?.email" v-bind="props">
        <template #title>
          <div v-if="item?.raw?.display_name" class="font-weight-bold pb-1">
            <span
              v-for="(seg, i) in parseDisplayName(
                item.raw.display_name,
                searchQuery
              )"
              :key="'dn-' + i"
              class="pointer-events-none"
              :class="seg.class"
              >{{ seg.text }}</span
            >
          </div>
          <span
            v-for="(seg, i) in parseEmail(item.value, searchQuery)"
            :key="'em-' + i"
            class="pointer-events-none"
            :class="[seg.class, { 'pointer-events-none': loading }]"
            data-test-id="email-suggestion"
            >{{ seg.text }}</span
          >
        </template>
        <template v-if="!item.raw?.isOrgMember" #append>
          <v-btn
            :aria-label="i18n.t('buttons.remove_signee')"
            class="text-grey mx-1"
            :class="{ 'cursor-not-allowed pointer-events-none': loading }"
            color="default"
            icon="fal fa-xmark"
            size="x-small"
            @click.stop="onDelete(item.raw?.email)"
          >
            <v-icon>fal fa-xmark</v-icon>
          </v-btn>
        </template>
      </v-list-item>
    </template>

    <!-- Groups section: only shown when 2+ chars typed and groups match -->
    <template #append-item>
      <template v-if="showGroupsSection && filteredUnits.length > 0">
        <v-divider v-if="emailSuggestionList.length > 0" class="mb-20" />
        <v-list-subheader>
          {{ i18n.t('labels.groups') }}
        </v-list-subheader>

        <!-- Loading state -->
        <div v-if="unitsLoading" class="d-flex justify-center py-4">
          <v-progress-circular color="primary" indeterminate size="24" />
        </div>

        <!-- Error state -->
        <v-alert
          v-else-if="unitsError"
          class="mx-4 mb-2"
          density="compact"
          type="error"
          variant="tonal"
        >
          {{ unitsError }}
        </v-alert>

        <!-- Filtered groups list -->
        <v-list-item
          v-for="unit in filteredUnits"
          v-else
          :key="unit.unitId"
          class="group-list-item"
          @click.prevent.stop="onGroupClick(unit.unitId, unit.displayName)"
        >
          <template #title>
            <span
              v-for="(seg, i) in parseDisplayName(
                unit.displayName,
                searchQuery
              )"
              :key="'group-' + i"
              class="font-weight-bold"
              :class="seg.class"
              >{{ seg.text }}</span
            >
          </template>
          <span class="members-amount">
            {{ totalMembers(unit) }} {{ i18n.t('labels.members') }}
          </span>
        </v-list-item>
      </template>
    </template>
  </v-combobox>
</template>

<script setup lang="ts">
import { v4 as uuid } from 'uuid';
import { useEmailService } from '@/components/inputs/Emailsuggestion/useEmailService';
import { useI18n } from 'vue-i18n';
import { useSuggestionHelper } from '@/components/inputs/Emailsuggestion/useSuggestionHelper';
import { computed, ref, watch } from 'vue';
import { refDebounced } from '@vueuse/core';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useGroupService } from '@/composables/useGroupService.ts';
import AuthService from '@/services/authService.ts';
import { useRoute } from 'vue-router';
import { EMAIL_REGEX } from '@deepcloud/field-validators';
import { vNoPasswordManager } from '@deepcloud/deep-ui-lib';
import { useEmitter } from '@/composables/useEmitter';

const i18n = useI18n();
const route = useRoute();
const emitter = useEmitter();

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
  'group-members-selected': [emails: string[]];
}>();

const props = defineProps({
  enableGroups: {
    type: Boolean,
    default: false
  }
});

const emailSuggestionList = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const hasFocused = ref(false);

const identifier = ref(getUuid());
const {
  filterItems: baseFilterItems,
  parseDisplayName,
  parseEmail
} = useSuggestionHelper();

function filterItems(
  itemText: string,
  queryText: string,
  item?: Record<string, unknown>
): boolean {
  if (validateEmail(queryText?.trim())) return false;
  return baseFilterItems(itemText, queryText, item);
}
const { currentDocument } = useDocumentContext();
const debouncedSearchQuery = refDebounced(searchQuery, 300);
const emailToHide = computed(
  () => currentDocument.value?.initiatorDisplayEmail || ''
);

const companyId = computed(() => currentDocument.value?.companyId || '');

const onPublicRoute = computed(
  () => typeof route.name === 'string' && route.name.includes('p-')
);

// On the public document route, organisation-scoped resources (e.g. org
// members) aren't available, but an authenticated user's own preference-based
// suggestions (recently used emails) still are, so suggestions should still
// be attempted for them.
const canShowEmailSuggestions = computed(
  () => !onPublicRoute.value || AuthService.isAuthenticated
);

const { getEmailSuggestionList, deleteRecentlyUsedEmails } = useEmailService(
  currentDocument.value?.companyId,
  emailToHide.value
);

const { units, unitsLoading, unitsError } = useGroupService(companyId.value);

const showGroupsSection = computed(
  () =>
    !onPublicRoute.value &&
    AuthService.isAuthenticated &&
    !!companyId.value &&
    props.enableGroups &&
    searchQuery.value.trim().length >= 2
);

// Filter units by search query, matching against the group displayName
const filteredUnits = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query || query.length < 2) return [];
  return units.value.filter((unit) =>
    unit.displayName.toLowerCase().includes(query)
  );
});

const hideNoData = computed(() => {
  const trimmed = searchQuery.value.trim();
  if (validateEmail(trimmed)) return true;
  if (trimmed.length < 2) return true;
  // Keep menu open for groups even while email API is loading
  if (filteredUnits.value.length > 0) return false;
  // No groups, hide menu while loading to prevent "No data" flash
  return !!loading.value;
});

const totalMembers = (unit: {
  memberSummary: { totalActiveMembers: number; totalPendingMembers: number };
}) =>
  unit.memberSummary.totalActiveMembers +
  unit.memberSummary.totalPendingMembers;

function onGroupClick(unitId: string, unitName: string): void {
  emitter.$emit('open-group-members-dialog', {
    unitId,
    unitName,
    onConfirm: (emails: string[]) => {
      emit('group-members-selected', emails);
    }
  });
}

async function onDelete(email: string): Promise<void> {
  await deleteRecentlyUsedEmails(email);
  await getEmailOptions(searchQuery.value);
}

async function getEmailOptions(query: string) {
  loading.value = true;
  const { isLoading, aggregatedList } = await getEmailSuggestionList(query);
  loading.value = isLoading;
  emailSuggestionList.value = aggregatedList as [];
}

function updateModelValue(value: string | null) {
  if (!value && canShowEmailSuggestions.value) {
    emailSuggestionList.value = [];
    getEmailOptions('');
  }
  emit('update:modelValue', value);
}

function validateEmail(mail: string) {
  return EMAIL_REGEX.test(mail);
}

function getUuid() {
  return uuid();
}

function onFocus(e: FocusEvent) {
  if (e?.target) {
    (e.target as HTMLInputElement).setAttribute('autocomplete', 'off');
  }
  hasFocused.value = true;
  if (validateEmail((e?.target as HTMLInputElement).value)) {
    emailSuggestionList.value = [];
  } else if (canShowEmailSuggestions.value) {
    getEmailOptions((e?.target as HTMLInputElement).value);
  }
}

watch(
  debouncedSearchQuery,
  (val) => {
    if (validateEmail(val)) {
      emailSuggestionList.value = [];
      return;
    }
    if (AuthService.isAuthenticated && canShowEmailSuggestions.value) {
      getEmailOptions(val);
    }
  },
  { deep: true, immediate: true }
);

watch(searchQuery, (val) => {
  if (val.trim().length >= 2) {
    const hasMatchingEmails = emailSuggestionList.value.some(
      (item) =>
        item.email &&
        item.email.toLowerCase().includes(val.trim().toLowerCase())
    );
    if (!hasMatchingEmails) {
      emailSuggestionList.value = [];
    }
  }
});
</script>

<style lang="scss" scoped>
.initiator :deep(input[type='email']) {
  color: rgb(158, 158, 158) !important;
  background-color: white !important;
}

.v-combobox--single.initiator:not(.v-combobox--selection-slot)
  :deep(.v-field--dirty:not(.v-field--focused) input) {
  opacity: 1;
}

.combobox {
  background-color: #fff;

  &:focus-within {
    border: none !important;
  }
}

.group-list-item {
  cursor: pointer;
  font-size: 14px;
  height: 52px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.003);
  }
}

.members-amount {
  font-size: 14px !important;
}
</style>
